"use client";

import React, { useState } from "react";
import { Loader, CircleCheck, CircleAlert, Send, Check } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const StyledInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input ref={ref} className={`flex h-12 w-full rounded-md border border-input bg-muted/30 px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`} {...props} />;
});
StyledInput.displayName = "StyledInput";
const StyledTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea ref={ref} className={`flex min-h-[120px] w-full rounded-md border border-input bg-muted/30 px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 resize-y ${className}`} {...props} />;
});
StyledTextarea.displayName = "StyledTextarea";
interface StyledCheckboxProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label: string;
  "data-index"?: number;
}
const StyledCheckbox = React.forwardRef<HTMLDivElement, StyledCheckboxProps>(({
  checked,
  onCheckedChange,
  label,
  ...props
}, ref) => {
  return <div data-section-id="29860" ref={ref} role="checkbox" aria-checked={checked} tabIndex={0} className={`group flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${checked ? "border-primary bg-primary/5" : "border-border bg-muted/20 hover:border-primary/50 hover:bg-muted/40"}`} onClick={() => onCheckedChange?.(!checked)} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onCheckedChange?.(!checked);
    }
  }} {...props}>
        <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border transition-colors ${checked ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/30 bg-transparent group-hover:border-primary"}`}>
          {checked && <Check className="h-3.5 w-3.5" />}
        </div>
        <span className={`text-sm font-medium leading-none ${checked ? "text-primary" : "text-muted-foreground group-hover:text-foreground"}`}>
          {label}
        </span>
      </div>;
});
StyledCheckbox.displayName = "StyledCheckbox";
function InitiateDominanceForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const serviceOptions = ["Professional Landscaping", "Interior & Exterior Painting", "High-Pressure Power Washing", "Janitorial & Cleaning Services", "Commercial Property Care", "Residential Maintenance", "Gutter & Roof Cleaning", "Window Washing", "Other"];
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center space-y-6 bg-primary/5 border border-primary/20 rounded-xl animate-in fade-in zoom-in-95 duration-500">
        <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
          <CircleCheck className="h-10 w-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Quote Request Received</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 text-primary hover:bg-primary/10">
          Request Another Quote
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Full Name</FormLabel>
              <FormControl>
                <StyledInput placeholder="John Smith" {...field} />
              </FormControl>
              <FormDescription>Enter the name of the primary property manager or owner.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Email Address</FormLabel>
              <FormControl>
                <StyledInput type="email" placeholder="john@company.com" {...field} />
              </FormControl>
              <FormDescription>We'll use this to send your customized maintenance proposal.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="phone" rules={{
      required: "Phone number is required",
      pattern: {
        value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Phone Number</FormLabel>
            <FormControl>
              <StyledInput type="tel" placeholder="(555) 000-0000" {...field} />
            </FormControl>
            <FormDescription>The best number to reach you for a quick property assessment.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="services_interested" rules={{
      required: "Please select at least one service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 text-lg font-bold block mb-4">Services Required</FormLabel>
            <FormControl>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {serviceOptions.map((service, i) => {
            const isChecked = Array.isArray(field.value) && field.value.includes(service);
            return <StyledCheckbox key={service} data-index={i} label={service} checked={isChecked} onCheckedChange={checked => {
              const current = Array.isArray(field.value) ? field.value : [];
              if (checked) {
                field.onChange([...current, service]);
              } else {
                field.onChange(current.filter((val: string) => val !== service));
              }
            }} />;
          })}
              </div>
            </FormControl>
            <FormDescription className="mt-2">Select the services you need for your residential or commercial property.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Property Details & Specific Requests</FormLabel>
            <FormControl>
              <StyledTextarea placeholder="Tell us about your property size, frequency of service required, or any specific maintenance challenges..." {...field} />
            </FormControl>
            <FormDescription>Optional, but helps us provide a more accurate initial estimate.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-3">
          <CircleAlert className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="font-medium text-destructive">Submission Error</h4>
            <p className="text-sm text-destructive/80">{submitError}</p>
          </div>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold shadow-xl shadow-primary/10 hover:shadow-primary/30 bg-primary text-primary-foreground transition-all duration-300 rounded-full shadow-[inset_0_1px_2px_rgba(255,255,255,0.3)]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Calculating Estimate...
          </> : <>
            Get a Free Quote <Send className="ml-2 h-5 w-5" />
          </>}
      </Button>
    </Form>;
}
export default function InitiateYourDominance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="get-your-maintenance-quote" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-foreground">
              Ready for Premium Property Care?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-medium">
              Complete the service request below. Our maintenance experts at Elyon Maintenance Inc. will analyze your requirements and engineer a custom roadmap for pristine facility care.
            </p>
          </div>

          <div className="bg-card/80 backdrop-blur-md border border-border/50 rounded-3xl p-6 md:p-10 shadow-2xl shadow-primary/5">
            <FormProvider formId="initiate_dominance_form_home" sectionName="initiate-your-dominance" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            services_interested: [],
            message: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            services_interested: {
              required: true
            }
          }} submitText="Get a Free Quote" successMessage="Your maintenance request has been received. Our property strategists are already reviewing your details.">
              <InitiateDominanceForm />
            </FormProvider>
          </div>
        </div>
      </div>
      <Toaster />
    </section>;
}